/* Zpráva k odkazu na hypoteční dotazník — co poradce opravdu pošle klientovi.
 *
 * PROČ TO EXISTUJE:
 * Vzorem je lib/crm/zpravaDoporuceni.js a stejná lekce z UAT 8/2026: do schránky
 * nepatří holá URL. Klient, kterému přistane ve WhatsAppu jenom odkaz, ho buď
 * neotevře, nebo ho otevře a nechápe, proč po něm někdo chce rodné číslo.
 * U hypotečního dotazníku je to vyhrocenější než u doporučení — ptáme se na
 * rodné číslo, příjem a zaměstnavatele. Bez věty „proč“ to vypadá jako phishing.
 *
 * APLIKACE NIC NEODESÍLÁ. Tenhle modul jen složí text. Odeslání dělá poradce
 * sám ve svém WhatsAppu / e-mailu. Je to vědomé rozhodnutí: kdyby odkaz na
 * dotazník s rodným číslem rozesílal server, stačila by jedna překlepnutá
 * adresa a citlivá výzva jde cizímu člověku.
 *
 * Modul je čistý (bez Reactu a bez API) — testovatelný a snadno přepsatelný.
 */

const POZDRAV = { cs: "Dobrý den", sk: "Dobrý deň" };

/* Kolik minut vyplnění zabere. Není to marketingové číslo — dotazník má kolem
 * devadesáti polí a většina z nich jsou údaje, které má klient po ruce
 * (občanka, výplatní páska, inzerát na byt). Patnáct minut je střízlivý odhad
 * pro člověka, který si k tomu sedne. Je to parametr, ne konstanta v textu,
 * aby si to poradce mohl přepsat, když bude dotazník kratší. */
const MINUT_VYCHOZI = 15;

/** ISO datum → „25. 8. 2026“. Nečitelné/chybějící datum vrací null,
 *  aby se do zprávy nikdy nedostalo „platí do Invalid Date“. */
export function datumCesky(iso, lang = "cs") {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang === "sk" ? "sk-SK" : "cs-CZ");
}

/**
 * Sestaví zprávu k odkazu na hypoteční dotazník.
 *
 * @param {object} o
 * @param {string} o.osloveni  jméno ve tvaru, jakým se má klient oslovit (vokativ řeší volající)
 * @param {string} o.odkaz     plná URL formuláře
 * @param {string} o.platiDo   ISO datum, do kdy odkaz funguje
 * @param {number} o.minut     odhad délky vyplňování
 * @param {string} o.poradce   jméno poradce (podpis); prázdné = bez podpisu
 * @param {string} o.lang      "cs" | "sk"
 * @returns {string} text připravený k vložení do WhatsApp / SMS / e-mailu
 *
 * Bez odkazu vrací prázdný řetězec — pozvánka bez URL nedává smysl a tiché
 * „pošli to bez odkazu“ by poradce zjistil až od zmateného klienta.
 */
export function zpravaDotaznik({
  osloveni = "",
  odkaz = "",
  platiDo = "",
  minut = MINUT_VYCHOZI,
  poradce = "",
  lang = "cs",
} = {}) {
  const url = String(odkaz ?? "").trim();
  if (!url) return "";

  const jmeno = String(osloveni ?? "").trim();
  const pozdrav = POZDRAV[lang] || POZDRAV.cs;
  const hlavicka = jmeno ? `${pozdrav} ${jmeno},` : `${pozdrav},`;
  const podpis = String(poradce ?? "").trim();
  const doKdy = datumCesky(platiDo, lang);
  /* Nesmyslné minuty (0, záporné, text) → vrátíme se k výchozímu odhadu.
     Radši realistický odhad než věta „zabere to 0 minut“. */
  const min = Number.isFinite(Number(minut)) && Number(minut) > 0 ? Math.round(Number(minut)) : MINUT_VYCHOZI;

  const radky = lang === "sk"
    ? [
        hlavicka,
        "",
        "posielam odkaz na dotazník k hypotéke. Banky odo mňa potrebujú rovnaké údaje od každého žiadateľa — keď ich budem mať pokope, porovnám ponuky viacerých bánk a nebudeme sa k tomu vracať na každom stretnutí.",
        "",
        `Vyplnenie zaberie asi ${min} minút. Čo neviete, nechajte prázdne, doplníme to spolu.`,
        "",
        url,
      ]
    : [
        hlavicka,
        "",
        "posílám odkaz na dotazník k hypotéce. Banky ode mě potřebují od každého žadatele tytéž údaje — až je budu mít pohromadě, porovnám nabídky několika bank a nebudeme se k tomu vracet na každé schůzce.",
        "",
        `Vyplnění zabere asi ${min} minut. Co nevíte, nechte prázdné, doplníme to spolu.`,
        "",
        url,
      ];

  /* Platnost odkazu se do zprávy dostane JEN když ji opravdu známe. Kdyby se
     datum nenačetlo, klient by dostal větu, které se nedá věřit — a přesně
     tahle věta ho má popohnat, aby dotazník neodložil na neurčito. */
  if (doKdy) {
    radky.push(
      "",
      lang === "sk"
        ? `Odkaz je len pre vás a funguje do ${doKdy}. Potom sa neotvorí a pošlem vám nový.`
        : `Odkaz je jen pro vás a funguje do ${doKdy}. Potom se neotevře a pošlu vám nový.`,
    );
  }

  radky.push(
    "",
    lang === "sk"
      ? "Údaje uvidím len ja, slúžia na prípravu žiadosti o hypotéku. Rodné číslo aj čísla dokladov ukladáme zašifrované."
      : "Údaje uvidím jen já, slouží k přípravě žádosti o hypotéku. Rodné číslo i čísla dokladů ukládáme zašifrovaně.",
  );

  if (podpis) {
    radky.push("", podpis);
  }
  return radky.join("\n");
}
